import { useState } from 'react'
import { apiPost, apiPatch } from '../../api'
import type { GiftRow } from '../../api'

interface Props {
  rows: GiftRow[]
  onChange: () => void
}

const STATUSES = ['idea', 'bought', 'given'] as const

function pounds(pence: number | null): string {
  if (pence === null) return '—'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)
}

export default function GiftIdeas({ rows, onChange }: Props) {
  const [filter, setFilter] = useState<'all' | typeof STATUSES[number]>('all')
  const [adding, setAdding] = useState(false)
  const [idea, setIdea] = useState('')
  const [budget, setBudget] = useState('')
  const [occasion, setOccasion] = useState('just because')

  const filtered = rows.filter(r => filter === 'all' || r.status === filter)

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!idea.trim()) return
    const pence = budget ? Math.round(parseFloat(budget) * 100) : undefined
    await apiPost('/home/gifts', { idea: idea.trim(), budget_pence: pence, occasion })
    setAdding(false); setIdea(''); setBudget(''); setOccasion('just because')
    onChange()
  }

  const advance = async (r: GiftRow) => {
    const idx = STATUSES.indexOf(r.status as typeof STATUSES[number])
    const next = STATUSES[Math.min(STATUSES.length - 1, idx + 1)] ?? 'given'
    await apiPatch(`/home/gifts/${r.id}`, { status: next })
    onChange()
  }

  return (
    <div className="gift-list">
      <div className="chip-row">
        {(['all', ...STATUSES] as const).map(f => (
          <button key={f} type="button" className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>{f}</button>
        ))}
        <button type="button" className="chip" onClick={() => setAdding(v => !v)}>{adding ? 'cancel' : '+ add'}</button>
      </div>
      {adding && (
        <form onSubmit={create} style={{ display: 'grid', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <input type="text" placeholder="Idea" value={idea} onChange={e => setIdea(e.target.value)} />
          <input type="number" inputMode="decimal" step="0.01" placeholder="Budget £ (optional)" value={budget} onChange={e => setBudget(e.target.value)} />
          <select value={occasion} onChange={e => setOccasion(e.target.value)}>
            <option value="just because">just because</option>
            <option value="anniversary">anniversary</option>
            <option value="birthday">birthday</option>
            <option value="christmas">christmas</option>
          </select>
          <button type="submit">Save</button>
        </form>
      )}
      {filtered.length === 0 && <div className="empty-state">No gift ideas.</div>}
      {filtered.map(r => (
        <div key={r.id} className="row">
          <span className="grow">
            {r.idea}
            {r.occasion && <span className="muted"> · {r.occasion}</span>}
          </span>
          <span className="mono muted">{pounds(r.budget_pence)}</span>
          <button type="button" className="pill" onClick={() => advance(r)} title="Advance status">{r.status}</button>
        </div>
      ))}
    </div>
  )
}
